import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Users, CreditCard, Layers, BarChart2, AlertTriangle,
  FileText, MessageSquare, Activity, Settings, ChevronRight, Bell, Search,
  LogOut, Building, Menu, X
} from 'lucide-react';
import SaaSOverview from './SaaSOverview';
import SaaSTenants from './SaaSTenants';
import SaaSSubscriptions from './SaaSSubscriptions';
import SaaSTemplateUsage from './SaaSTemplateUsage';
import SaaSUsage from './SaaSUsage';
import SaaSErrors from './SaaSErrors';
import SaaSLogs from './SaaSLogs';
import SaaSTickets from './SaaSTickets';
import SaaSHealth from './SaaSHealth';
import SaaSSettings from './SaaSSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/admin/saas' },
  { key: 'tenants', label: 'Tenants', icon: Users, path: '/admin/saas/tenants' },
  { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard, path: '/admin/saas/subscriptions' },
  { key: 'templates', label: 'Templates', icon: Layers, path: '/admin/saas/templates' },
  { key: 'usage', label: 'Usage', icon: BarChart2, path: '/admin/saas/usage' },
  { key: 'errors', label: 'Errors', icon: AlertTriangle, path: '/admin/saas/errors' },
  { key: 'logs', label: 'Logs', icon: FileText, path: '/admin/saas/logs' },
  { key: 'tickets', label: 'Support Tickets', icon: MessageSquare, path: '/admin/saas/support' },
  { key: 'health', label: 'System Health', icon: Activity, path: '/admin/saas/health' },
  { key: 'settings', label: 'Settings', icon: Settings, path: '/admin/saas/settings' },
];

export default function SaaSAdminPanel() {
  const location = useLocation();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchKPIs();
  }, []);

  const fetchKPIs = async () => {
    try {
      const response = await axios.get(`${API}/saas/kpis`);
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  const getCurrentNavItem = () => {
    const path = location.pathname;
    if (path === '/admin/saas' || path === '/admin/saas/') return 'overview';
    const item = NAV_ITEMS.find(n => path.startsWith(n.path) && n.path !== '/admin/saas');
    return item?.key || 'overview';
  };

  return (
    <div className="saas-admin-panel" data-testid="saas-admin-panel">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-overlay saas-overlay"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="saas-mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside className={`saas-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="saas-sidebar-header">
          <Building size={28} />
          <div>
            <h1>DepinaCRM</h1>
            <span>SaaS Control Panel</span>
          </div>
          <button className="saas-mobile-close mobile-only" onClick={() => setMobileMenuOpen(false)} data-testid="saas-mobile-close">
            <X size={20} />
          </button>
        </div>

        <nav className="saas-nav">
          {NAV_ITEMS.map(item => {
            const isActive = getCurrentNavItem() === item.key;
            const IconComponent = item.icon;
            
            return (
              <Link
                key={item.key}
                to={item.path}
                className={`saas-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.key}`}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
                {item.key === 'errors' && kpis?.total_unresolved_errors > 0 && (
                  <span className="nav-badge error">{kpis.total_unresolved_errors}</span>
                )}
                {item.key === 'tickets' && kpis?.open_support_tickets > 0 && (
                  <span className="nav-badge">{kpis.open_support_tickets}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="saas-sidebar-footer">
          <Link to="/" className="saas-nav-item" onClick={() => setMobileMenuOpen(false)}>
            <LogOut size={20} />
            <span>Exit to Platform</span>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="saas-main">
        {/* Top Bar */}
        <header className="saas-topbar">
          <div className="saas-topbar-left">
            <button
              className="mobile-hamburger saas-hamburger mobile-only"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="saas-mobile-hamburger"
            >
              <Menu size={22} />
            </button>
            <h2>{NAV_ITEMS.find(n => n.key === getCurrentNavItem())?.label || 'Overview'}</h2>
          </div>
          <div className="saas-topbar-right">
            <div className="saas-search">
              <Search size={18} />
              <input type="text" placeholder="Search tenants, tickets..." />
            </div>
            <button className="saas-icon-btn">
              <Bell size={20} />
              {kpis?.errors_today > 0 && <span className="badge">{kpis.errors_today}</span>}
            </button>
            <div className="saas-admin-avatar">SA</div>
          </div>
        </header>

        {/* Page Content */}
        <div className="saas-content">
          <Routes>
            <Route index element={<SaaSOverview />} />
            <Route path="tenants" element={<SaaSTenants />} />
            <Route path="tenants/:tenantId" element={<SaaSTenants />} />
            <Route path="subscriptions" element={<SaaSSubscriptions />} />
            <Route path="templates" element={<SaaSTemplateUsage />} />
            <Route path="usage" element={<SaaSUsage />} />
            <Route path="errors" element={<SaaSErrors />} />
            <Route path="logs" element={<SaaSLogs />} />
            <Route path="support" element={<SaaSTickets />} />
            <Route path="support/:ticketId" element={<SaaSTickets />} />
            <Route path="health" element={<SaaSHealth />} />
            <Route path="settings" element={<SaaSSettings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
